/**
 * Live chatter config for lead rooms (Sprint 3b).
 * Rooms feel alive: a constant stream of NPC banter (advocate / cynic / panic /
 * analysis), plus occasional RUMORS with a hidden truth the player fact-checks
 * via an ad. Chatter category weights react to the room personality + the live
 * call direction + the last resolution. All text is i18n keys (no hardcoding).
 */
import type { RoomPersonality } from './leadRooms.js';
import { mulberry32 } from './comments.js';

/** Chatter tone buckets. Each maps to i18n pools chat_{cat}_{n}. */
export type ChatterCategory = 'advocate' | 'cynic' | 'panic' | 'analysis';
export const CHATTER_CATEGORIES: readonly ChatterCategory[] = ['advocate', 'cynic', 'panic', 'analysis'];

/** i18n pool size per category (chat_advocate_1..N …) */
export const CHATTER_POOL_SIZE = 50;

export const CHATTER_CONFIG = {
  /** ms between chatter emissions for the room being viewed */
  emitIntervalMin: 2_000,
  emitIntervalMax: 5_000,
  /** background (unviewed) rooms emit far more slowly (keeps memory/save calm) */
  backgroundMultiplier: 6,
  /** messages already present when a room is first entered (feels lived-in) */
  seedCount: 8,
  /** ring-buffer cap of stored chat messages per room */
  maxChat: 15,
  /** chance a given emission is a RUMOR rather than plain banter */
  rumorChance: 0.12,
  /** max concurrent unresolved rumors per room */
  maxRumors: 2,
  /** a rumor stays live (fact-checkable) this long before it self-resolves */
  rumorLifetime: 45_000,
  /**
   * Front-running window: when a genuine rumor is CONFIRMED early via a fact
   * check, its real price move is scheduled this far in the future (15~30s) so
   * the informed player can enter before the crowd. "탈출은 지능순" as a mechanic.
   */
  factCheckMoveDelayMin: 15_000,
  factCheckMoveDelayMax: 30_000,
  /** how long a player reaction's NPC echoes take to appear */
  reactionEchoDelayMin: 1_500,
  reactionEchoDelayMax: 4_000,
  reactionEchoMin: 1,
  reactionEchoMax: 2,
} as const;

/**
 * Player reaction choices (static taps, no free typing). Each biases the NPC
 * echo pool by mood. Pure flavor - no market impact.
 */
export type ReactionMood = 'up' | 'down' | 'neutral';
export interface ReactionChoice {
  /** i18n key for the button + the posted message */
  key: string;
  mood: ReactionMood;
}
export const REACTION_CHOICES: readonly ReactionChoice[] = [
  { key: 'rc_moon', mood: 'up' },      // "가즈아"
  { key: 'rc_cut', mood: 'down' },     // "손절함"
  { key: 'rc_watch', mood: 'neutral' },// "관망"
] as const;

/** echo pool key per mood (chat_echo_{pool}_{n}), 16 each */
export const ECHO_POOL_BY_MOOD: Record<ReactionMood, string> = {
  up: 'up',
  down: 'down',
  neutral: 'neut',
};
export const ECHO_POOL_SIZE = 16;

export function getReaction(idx: number): ReactionChoice | undefined {
  return REACTION_CHOICES[idx];
}

/**
 * Rumor kinds. Each maps to i18n rumor_{kind}_{variant} with a {coin}
 * placeholder and a price DIRECTION the rumor implies (used to reserve a real
 * move when genuine). Each kind has `variants` phrasings drawn via a shuffle bag
 * so the same wording does not repeat before the pool is exhausted.
 */
export interface RumorTemplate {
  /** i18n key prefix = 'rumor_' + id (+ '_' + variant) */
  id: string;
  direction: 1 | -1;
  /** momentum push (units of coin volatility) applied when genuine + resolved */
  impact: number;
  /** how long the reserved price move lasts once it fires (ms) */
  moveDuration: number;
  /** number of phrasing variants (rumor_{id}_1 .. rumor_{id}_{variants}) */
  variants: number;
}
/** phrasing variants per rumor kind (rumor_{kind}_1 .. rumor_{kind}_{N}) */
export const RUMOR_VARIANTS = 8;
export const RUMOR_TEMPLATES: readonly RumorTemplate[] = [
  { id: 'listing', direction: 1, impact: 2.4, moveDuration: 30_000, variants: RUMOR_VARIANTS },
  { id: 'accumulate', direction: 1, impact: 2.0, moveDuration: 26_000, variants: RUMOR_VARIANTS },
  { id: 'partnership', direction: 1, impact: 1.8, moveDuration: 24_000, variants: RUMOR_VARIANTS },
  { id: 'delist', direction: -1, impact: 2.4, moveDuration: 30_000, variants: RUMOR_VARIANTS },
  { id: 'rugpull', direction: -1, impact: 2.6, moveDuration: 28_000, variants: RUMOR_VARIANTS },
  { id: 'dump', direction: -1, impact: 2.0, moveDuration: 24_000, variants: RUMOR_VARIANTS },
] as const;

/**
 * Base probability that a rumor from a room is GENUINE, by personality.
 * Collusion rooms plant traps -> mostly false. Honest rooms -> mostly true.
 */
export const RUMOR_TRUTH_BY_PERSONALITY: Record<RoomPersonality, number> = {
  honest: 0.7,
  hype: 0.45,
  collusion: 0.25,
};

/** Decide a rumor's hidden truth from its room personality + a roll (pure). */
export function rollRumorGenuine(personality: RoomPersonality, roll: number): boolean {
  return roll < RUMOR_TRUTH_BY_PERSONALITY[personality];
}

/**
 * Category weights for the next banter line, shaped by the room personality,
 * the live call direction (bull=1 / bear=-1 / 0=none) and the last resolution
 * ('hit' | 'miss' | undefined). Returns a weight per category (>= 0). Pure.
 */
export function chatterWeights(
  personality: RoomPersonality,
  callDir: 1 | -1 | 0,
  lastOutcome: 'hit' | 'miss' | undefined
): Record<ChatterCategory, number> {
  // baseline: some of everything so a room never feels one-note
  const w: Record<ChatterCategory, number> = {
    advocate: 2, cynic: 2, panic: 1, analysis: 2,
  };

  // live bullish call -> advocacy + analysis rise
  if (callDir === 1) { w.advocate += 4; w.analysis += 2; }
  // live bearish call -> panic + cynicism rise
  if (callDir === -1) { w.panic += 3; w.cynic += 2; }

  // personality colouring
  if (personality === 'hype') { w.advocate += 3; w.panic -= 0; }
  if (personality === 'collusion') { w.advocate += 4; w.cynic += 2; } // pump spam laced with doubters
  if (personality === 'honest') { w.analysis += 3; w.cynic += 1; }

  // reaction to the last resolved call
  if (lastOutcome === 'hit') { w.advocate += 3; w.analysis += 1; }
  if (lastOutcome === 'miss') { w.panic += 3; w.cynic += 3; }

  for (const c of CHATTER_CATEGORIES) w[c] = Math.max(0, w[c]);
  return w;
}

/** Weighted category pick from a [0,1) roll (pure - deterministic in the roll). */
export function pickCategory(
  weights: Record<ChatterCategory, number>,
  roll: number
): ChatterCategory {
  const total = CHATTER_CATEGORIES.reduce((s, c) => s + weights[c], 0);
  if (total <= 0) return 'analysis';
  let acc = 0;
  const target = roll * total;
  for (const c of CHATTER_CATEGORIES) {
    acc += weights[c];
    if (target < acc) return c;
  }
  return CHATTER_CATEGORIES[CHATTER_CATEGORIES.length - 1];
}

/**
 * Deterministic display nickname index for a chat author from its id.
 * (Reuses the shared NPC_NICKS pool via mulberry32; the caller maps to names.)
 */
export function nickIdxFor(seed: string, nickCount: number): number {
  return Math.floor(mulberry32(hashStr(seed))() * nickCount);
}

/** small stable string hash for nick seeding */
function hashStr(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
